import classnames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import InnerImageZoom from "react-inner-image-zoom";
import "react-inner-image-zoom/lib/InnerImageZoom/styles.css";
import SwiperCore, { Navigation, Thumbs } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";

import { useIsMobile } from "@utils/hooks/useIsMobile.hook";
import { stopVideo } from "@utils/imageViewer";
import determineSlidesPerView from "@utils/swiper";

import BreakpointsService, {
    BreakpointMaxSizes,
} from "@services/breakpoint.service";

import { SlideThumbnail } from "@components/Product/ImageViewer/partials/SlideThumbnail/slideThumbnail.component";
import { SwiperNavButton } from "@components/Product/ImageViewer/partials/SwiperNavButton/swiperNavButton.component";

import "./../../../styles/styleguide/vendor-overrides/innerImageZoom/_custom.scss";
import "./imageViewerFullWidth.scss";

type Props = {
    images: any[];
    videos: any[];
    activeIndex: number;
    setActiveIndex: (index: number) => void;
    defaultVideoThumbnailPath?: string;
};

const breakpoints = [
    { slides: "3.6", direction: "horizontal", breakpoint: "xs" },
    { slides: "3.6", direction: "horizontal", breakpoint: "sm" },
    { slides: "3.6", direction: "horizontal", breakpoint: "md" },
    { slides: "5", direction: "horizontal", breakpoint: "lg" },
    { slides: "auto", direction: "vertical", breakpoint: "xl" },
];

const ImageViewerFullWidth = ({
    images,
    videos,
    activeIndex,
    setActiveIndex,
    defaultVideoThumbnailPath,
}: Props) => {
    const [mainSwiper, setMainSwiper] = useState<SwiperCore>();
    const [thumbSwiper, setThumbSwiper] = useState<SwiperCore>();

    const isMobile = useIsMobile();

    const navigationPrevRef = useRef(null);
    const navigationNextRef = useRef(null);

    const amount = images.concat(videos).length;

    useEffect(() => {
        const hashChangeHandler = () => {
            stopVideo(true);
        };

        window.addEventListener("hashchange", hashChangeHandler);

        return () => {
            window.removeEventListener("hashchange", hashChangeHandler);
        };
    }, []);

    useEffect(() => {
        if (mainSwiper && thumbSwiper) {
            if (mainSwiper.activeIndex !== activeIndex) {
                mainSwiper.slideTo(activeIndex);
                thumbSwiper.update();
            }
        }
    }, [activeIndex]);

    return (
        <div className="row imageViewerFullWidth-root">
            <div />
            <div className="col-xl-10 p-0 pr-xl-4 imageViewerFullWidth-centered">
                <div className={"d-flex align-items-center mw-100"}>
                    {amount > 1 && (
                        <SwiperNavButton
                            className="d-none d-md-block"
                            innerRef={navigationPrevRef}
                            disabled={activeIndex === 0}
                            icon="left"
                        />
                    )}
                    <Swiper
                        modules={[Thumbs, Navigation]}
                        onSwiper={setMainSwiper}
                        initialSlide={activeIndex}
                        onActiveIndexChange={swiper => {
                            setActiveIndex(swiper.activeIndex);
                            stopVideo(true);
                        }}
                        thumbs={{ swiper: thumbSwiper }}
                        allowTouchMove
                        zoom={false}
                        navigation={{
                            nextEl: navigationNextRef.current,
                            prevEl: navigationPrevRef.current,
                        }}
                        slidesPerView={"auto"}
                        spaceBetween={5}
                    >
                        {images &&
                            images.map((image, index) => (
                                <SwiperSlide
                                    className="imageViewerFullWidth__gallery-slide"
                                    key={"imageViewerFullWidth-main" + index}
                                >
                                    <InnerImageZoom
                                        src={isMobile ? image.main : image.zoom}
                                        className="imageViewerFullWidth__image"
                                        zoomSrc={
                                            isMobile
                                                ? image.zoom
                                                : image.maxZoom
                                        }
                                        //@ts-ignore
                                        alt={image.altText}
                                        zoomPreload
                                        fullscreenOnMobile={true}
                                        hasSpacer={true}
                                        mobileBreakpoint={BreakpointsService.getBreakpointMaxValues(
                                            BreakpointMaxSizes.md,
                                        )}
                                    />
                                </SwiperSlide>
                            ))}
                        {videos &&
                            videos.map((video, index) => (
                                <SwiperSlide
                                    key={
                                        "imageViewerFullWidth-main-video" +
                                        index
                                    }
                                    className="imageViewerFullWidth__gallery-slide
                                            imageViewerFullWidth-mediaSlide
                                            embed-responsive embed-responsive-16by9"
                                >
                                    <iframe
                                        className="d-block w-100 h-100 product-video--full-width"
                                        id={`imageViewerFullWidthIframe-${index}`}
                                        src={`${video.main}`}
                                    />
                                </SwiperSlide>
                            ))}
                    </Swiper>
                    {amount > 1 && (
                        <SwiperNavButton
                            className="d-none d-md-block"
                            innerRef={navigationNextRef}
                            disabled={activeIndex + 1 === amount}
                            icon="right"
                        />
                    )}
                </div>
            </div>

            <div className="col-xl-2 p-0 imageViewerFullWidth-thumbnailSlider">
                <div className="imageViewerFullWidth__thumbnail-slider">
                    {amount > 1 && (
                        <SwiperNavButton
                            disabled={activeIndex === 0}
                            icon="up"
                            onClick={() => {
                                if (activeIndex !== 0) {
                                    mainSwiper && mainSwiper.slidePrev();
                                    thumbSwiper && thumbSwiper.update();
                                }
                            }}
                            className="navButton navButtonPrev"
                        />
                    )}
                    <Swiper
                        modules={[Thumbs, Navigation]}
                        className="imageViewerFullWidth-swipercontainer"
                        onSwiper={setThumbSwiper}
                        spaceBetween={10}
                        slidesPerView={"auto"}
                        breakpoints={determineSlidesPerView(breakpoints)}
                        watchSlidesProgress={true}
                    >
                        {images &&
                            videos &&
                            images.concat(videos).map((media, index) => (
                                <SwiperSlide
                                    className={classnames(
                                        "imageViewerFullWidth__thumb-slide",
                                        "imageViewerFullWidth-thumbnail",
                                        index === activeIndex &&
                                            "imageViewerFullWidth-activeThumb",
                                    )}
                                    key={"imageViewerFullWidth-thumbs" + index}
                                    onClick={() => {
                                        setActiveIndex(index);
                                    }}
                                >
                                    <SlideThumbnail
                                        lazyLoad={true}
                                        media={media}
                                        defaultVideoThumbnailPath={
                                            defaultVideoThumbnailPath
                                        }
                                    />
                                </SwiperSlide>
                            ))}
                    </Swiper>
                    {amount > 1 && (
                        <SwiperNavButton
                            disabled={activeIndex + 1 === amount}
                            icon="down"
                            onClick={() => {
                                if (activeIndex + 1 !== amount) {
                                    mainSwiper && mainSwiper.slideNext();
                                    thumbSwiper && thumbSwiper.update();
                                }
                            }}
                            className="navButton navButtonNext"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export { ImageViewerFullWidth };
